/**
 * OrganisationWizard — guided creation: one decision at a time.
 * Step 1 asks what you are creating, the parent steps walk down the ladder
 * showing only the eligible bodies at that level, then the level's own fields,
 * then a review of the full path before anything is saved.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2, ArrowLeft, ArrowRight, Check, ChevronRight, Globe2, Flag, Landmark,
  MapPin, Church, Sprout, GraduationCap, HeartHandshake, Home, Plus, AlertTriangle,
} from 'lucide-react';

const FIELD_LABELS = {
  country: 'Country', region: 'Region / State', district: 'District', city: 'City',
  address: 'Address', currency: 'Base currency', website_url: 'Website',
  contact_name: 'Contact name', contact_email: 'Contact email', contact_phone: 'Contact phone',
  leader_name: 'Leader', overseer_name: 'Overseer', meeting_day: 'Meeting day',
  service_times: 'Service times', registration_number: 'Registration number',
  hq_address: 'HQ address',
};

const PRIMARY_CHOICES = [
  { key: 'hq', type: 'global_ministry', title: 'HQ', icon: Globe2,
    blurb: 'The global headquarters — the very top of the ministry.' },
  { key: 'country_office', type: 'country', title: 'Country Office', icon: Flag,
    blurb: 'A national office overseeing the work in one country.' },
  { key: 'province', type: 'province', title: 'Province', icon: Landmark,
    blurb: 'A province or region grouping districts and assemblies.' },
  { key: 'assembly', type: 'assembly', title: 'Assembly', icon: Church,
    blurb: 'An established local church with its own congregation.' },
  { key: 'church_plant', type: 'assembly', plant: true, title: 'Church Plant', icon: Sprout,
    blurb: 'A new church being planted — it graduates into a full assembly later.' },
];

const MORE_CHOICES = [
  { key: 'district', type: 'district', title: 'District', icon: MapPin,
    blurb: 'A cluster of assemblies inside a province.' },
  { key: 'bible_college_campus', type: 'bible_college_campus', title: 'Bible College Campus',
    icon: GraduationCap, blurb: 'A training campus run by the ministry.' },
  { key: 'ministry', type: 'ministry', title: 'Ministry / Department', icon: HeartHandshake,
    blurb: 'A department such as Youth, Women or Media.' },
  { key: 'home_group', type: 'home_group', title: 'Home Group', icon: Home,
    blurb: 'A small group meeting in a home.' },
  { key: 'cell', type: 'cell', title: 'Cell', icon: Home,
    blurb: 'The smallest unit, inside a home group or assembly.' },
];

// The descent each type walks down. 'global_ministry' is prepended for every
// non-root type so the wizard always starts from the top of the tree.
const DESCENT = {
  global_ministry: [],
  // A Country Office normally hangs off HQ, but a multi-country Province may
  // own one — so we ask, defaulting to HQ.
  country: ['province'],
  province: ['country'],
  district: ['country', 'province'],
  assembly: ['country', 'province', 'district'],
  bible_college_campus: ['country', 'province'],
  ministry: ['country', 'province', 'assembly'],
  home_group: ['country', 'province', 'assembly'],
  cell: ['country', 'province', 'assembly', 'home_group'],
};

const chainFor = (type) =>
  type === 'global_ministry' ? [] : ['global_ministry', ...DESCENT[type]];

const StepRail = ({ steps, active }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid="wizard-step-rail">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
        <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${
          i === active ? 'bg-slate-900 text-white'
            : i < active ? 'bg-emerald-50 text-emerald-700' : 'text-slate-400'}`}>
          {i < active && <Check className="h-3 w-3 inline mr-1 -mt-0.5" />}{s}
        </span>
      </React.Fragment>
    ))}
  </div>
);

const OptionCard = ({ icon: Icon, title, blurb, onClick, testId, muted }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900
               hover:shadow-[0_8px_30px_-12px_rgba(15,23,42,0.35)] transition-all duration-200
               focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
  >
    <div className="flex items-start gap-3">
      {Icon && (
        <span className={`shrink-0 rounded-lg p-2 ${muted ? 'bg-slate-100' : 'bg-slate-900'}`}>
          <Icon className={`h-4 w-4 ${muted ? 'text-slate-500' : 'text-white'}`} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        {blurb && <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{blurb}</span>}
      </span>
      <ArrowRight className="h-4 w-4 text-slate-300 ml-auto shrink-0 group-hover:text-slate-900
                             group-hover:translate-x-0.5 transition-all" />
    </div>
  </button>
);

export const OrganisationWizard = ({ open, onOpenChange, defaultParent, onCreated }) => {
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState(null);       // one of PRIMARY/MORE_CHOICES
  const [picks, setPicks] = useState([]);           // chosen ancestors, top-down
  const [parentNode, setParentNode] = useState(null); // resolved parent (null = root)
  const [phase, setPhase] = useState('type');       // type | parent | details | review
  const [showMore, setShowMore] = useState(false);
  const [form, setForm] = useState({});
  const [countryDraft, setCountryDraft] = useState('');

  const { data: meta } = useQuery({
    queryKey: ['org-types'],
    queryFn: async () => (await api.get('/admin/organisations/meta/types')).data,
    enabled: open,
  });
  const { data: flat = [] } = useQuery({
    queryKey: ['org-flat'],
    queryFn: async () => (await api.get('/admin/organisations')).data,
    enabled: open,
  });

  const types = meta?.types || [];
  const typeDef = types.find((t) => t.key === choice?.type);
  const chain = choice ? chainFor(choice.type) : [];
  const byId = useMemo(() => Object.fromEntries(flat.map((o) => [o.id, o])), [flat]);

  const isDescendant = (node, baseId) => {
    let cur = node;
    let hops = 0;
    while (cur?.parent_id && hops < 12) {
      if (cur.parent_id === baseId) return true;
      cur = byId[cur.parent_id];
      hops += 1;
    }
    return false;
  };

  const candidates = (levelType, base) =>
    flat.filter((o) => o.organisation_type === levelType
      && o.status !== 'archived'
      && (!base ? true : isDescendant(o, base.id)));

  const skipAllowed = (base) => !!base && (typeDef?.parents || []).includes(base.organisation_type);

  // Walk down as far as the data allows, only stopping where a human decision
  // is genuinely needed.
  const resolve = (startPicks) => {
    const next = [...startPicks];
    for (let level = next.length; level < chain.length; level += 1) {
      const base = next[level - 1] || null;
      const cands = candidates(chain[level], base);
      if (cands.length === 0) return { picks: next, level, cands, done: false };
      if (cands.length === 1 && !skipAllowed(base)) { next.push(cands[0]); continue; }
      return { picks: next, level, cands, done: false };
    }
    return { picks: next, level: chain.length, cands: [], done: true };
  };

  const walk = choice ? resolve(picks) : { picks: [], level: 0, cands: [], done: true };
  const stepBase = walk.picks[walk.level - 1] || null;

  const reset = () => {
    setChoice(null); setPicks([]); setParentNode(null); setPhase('type');
    setShowMore(false); setForm({}); setCountryDraft('');
  };

  useEffect(() => { if (open) reset(); }, [open]);

  // Launched from "Add child organisation" on a tree row: only the types that
  // may legally sit under that body are offered.
  const childChoices = useMemo(() => {
    if (!defaultParent || !types.length) return null;
    return [...PRIMARY_CHOICES, ...MORE_CHOICES].filter((c) =>
      (types.find((t) => t.key === c.type)?.parents || [])
        .includes(defaultParent.organisation_type));
  }, [defaultParent, types]);

  const startDetails = (parent) => {
    setParentNode(parent);
    const countryPick = [...(walk.picks || [])].reverse()
      .find((p) => p?.organisation_type === 'country');
    setForm({
      name: '', code: '', description: '', leader_name: '', leader_title: '',
      countries: [],
      country: countryPick?.country || countryPick?.name || parent?.country || '',
    });
    setPhase('details');
  };

  const pickChoice = (c) => {
    setChoice(c);
    if (defaultParent) {
      const legal = (types.find((t) => t.key === c.type)?.parents || [])
        .includes(defaultParent.organisation_type);
      if (legal) {
        setPicks([]);
        setParentNode(defaultParent);
        setForm({
          name: '', code: '', description: '', leader_name: '', leader_title: '',
          countries: [], country: defaultParent.country || '',
        });
        setPhase('details');
        return;
      }
    }
    setPicks([]);
    setPhase(c.type === 'global_ministry' ? 'details' : 'parent');
    if (c.type === 'global_ministry') {
      setParentNode(null);
      setForm({ name: '', code: '', description: '', leader_name: '', leader_title: '', countries: [] });
    }
  };

  // Once the walk has nothing left to ask, move straight on to the fields.
  useEffect(() => {
    if (phase !== 'parent' || !choice) return;
    if (walk.done) { startDetails(walk.picks[walk.picks.length - 1] || null); return; }
    // Nothing exists at this level and the current body is already a legal
    // parent — that is not a decision, so don't ask for it.
    if (walk.cands.length === 0 && skipAllowed(stepBase)) startDetails(stepBase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, walk.done, walk.cands.length, choice, flat.length]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.post('/admin/organisations', payload)).data,
    onSuccess: (res) => {
      toast.success(`${res.name} created`);
      ['org-tree', 'org-flat', 'admin-churches', 'org-leaders'].forEach((k) =>
        queryClient.invalidateQueries({ queryKey: [k] }));
      onOpenChange(false);
      // A new assembly still needs its leader, service times, contact and
      // public page — offer that straight away.
      if (res.organisation_type === 'assembly') onCreated?.(res);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const path = useMemo(() => {
    const trail = [];
    let cur = parentNode;
    let hops = 0;
    while (cur && hops < 12) { trail.unshift(cur); cur = byId[cur.parent_id]; hops += 1; }
    return trail;
  }, [parentNode, byId]);

  const submit = () => {
    const payload = {
      name: (form.name || '').trim(),
      organisation_type: choice.type,
      parent_id: parentNode?.id || null,
      status: 'active',
    };
    if (choice.plant) payload.is_church_plant = true;
    if ((form.code || '').trim()) payload.code = form.code.trim();
    (typeDef?.fields || []).forEach((f) => {
      if (f !== 'countries' && form[f]) payload[f] = form[f];
    });
    if ((form.countries || []).length) payload.countries = form.countries;
    ['leader_name', 'leader_title', 'description'].forEach((f) => {
      if (form[f]) payload[f] = form[f];
    });
    save.mutate(payload);
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const stepNames = ['What', 'Where', 'Details', 'Review'];
  const activeStep = { type: 0, parent: 1, details: 2, review: 3 }[phase];
  const levelLabel = (key) => types.find((t) => t.key === key)?.label || key;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="organisation-wizard">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-lg">
            {choice ? `New ${choice.title}` : 'Add to the ministry'}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {phase === 'type' && 'Answer one question at a time — we build the hierarchy for you.'}
            {phase === 'parent' && `Choose where this ${choice?.title.toLowerCase()} belongs.`}
            {phase === 'details' && 'Only the fields this level needs.'}
            {phase === 'review' && 'Check the path, then save.'}
          </DialogDescription>
          <StepRail steps={stepNames} active={activeStep} />
        </DialogHeader>

        {/* ── Step 1: what are you creating? ── */}
        {phase === 'type' && (
          <div className="space-y-3" data-testid="wizard-step-type">
            {defaultParent && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                Adding inside <strong className="text-slate-900">{defaultParent.name}</strong> · {defaultParent.type_label}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(childChoices || PRIMARY_CHOICES).map((c) => (
                <OptionCard key={c.key} icon={c.icon} title={c.title} blurb={c.blurb}
                            testId={`wizard-choice-${c.key}`} onClick={() => pickChoice(c)} />
              ))}
              {childChoices?.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="wizard-no-child-types">
                  Nothing can be created inside a {defaultParent.type_label}.
                </p>
              )}
            </div>
            {childChoices ? null : showMore ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1" data-testid="wizard-more-types">
                {MORE_CHOICES.map((c) => (
                  <OptionCard key={c.key} icon={c.icon} title={c.title} blurb={c.blurb} muted
                              testId={`wizard-choice-${c.key}`} onClick={() => pickChoice(c)} />
                ))}
              </div>
            ) : (
              <button type="button" onClick={() => setShowMore(true)}
                      data-testid="wizard-show-more-types"
                      className="text-xs font-medium text-slate-500 hover:text-slate-900 underline underline-offset-4">
                More types (district, campus, ministry, home group, cell)
              </button>
            )}
          </div>
        )}

        {/* ── Step 2: where does it belong? ── */}
        {phase === 'parent' && !walk.done && (
          <div className="space-y-3" data-testid="wizard-step-parent">
            {walk.picks.length > 0 && (
              <p className="text-xs text-slate-500" data-testid="wizard-parent-trail">
                {walk.picks.map((p) => p.name).join('  →  ')}
              </p>
            )}
            <p className="text-sm font-semibold text-slate-900">
              {choice?.type === 'country'
                ? 'Where should this Country Office sit?'
                : `Which ${levelLabel(chain[walk.level])}${stepBase ? ` in ${stepBase.name}` : ''}?`}
            </p>

            {walk.cands.length === 0 && !skipAllowed(stepBase) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                   data-testid="wizard-parent-blocked">
                <p className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  There is no {levelLabel(chain[walk.level])} yet.
                </p>
                <p className="text-xs mt-1 text-amber-800">
                  Create one first — this wizard can start it for you.
                </p>
                <Button size="sm" variant="outline" className="mt-3 bg-white"
                        data-testid="wizard-create-missing-parent"
                        onClick={() => {
                          const target = [...PRIMARY_CHOICES, ...MORE_CHOICES]
                            .find((c) => c.type === chain[walk.level] && !c.plant);
                          if (target) { setPicks([]); pickChoice(target); }
                        }}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Create a {levelLabel(chain[walk.level])}
                </Button>
              </div>
            )}

            {walk.cands.length === 0 && skipAllowed(stepBase) && (
              <p className="text-xs text-slate-500" data-testid="wizard-parent-none-yet">
                No {levelLabel(chain[walk.level])} is recorded in {stepBase.name} yet — attach it
                directly instead.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {choice?.type === 'country' && skipAllowed(stepBase) && (
                <OptionCard icon={Check}
                            title={`Directly under ${stepBase.name}`}
                            blurb="The usual choice for a national office."
                            testId="wizard-parent-attach-here"
                            onClick={() => startDetails(stepBase)} />
              )}
              {walk.cands.map((o) => (
                <OptionCard key={o.id} title={o.name}
                            blurb={[o.type_label, o.country].filter(Boolean).join(' · ')}
                            testId={`wizard-parent-option-${o.id}`}
                            onClick={() => setPicks([...walk.picks, o])} />
              ))}
              {skipAllowed(stepBase) && choice?.type !== 'country' && (
                <OptionCard muted icon={Check}
                            title={`Attach straight to ${stepBase.name}`}
                            blurb={choice?.type === 'province'
                              ? 'Use this for a province that serves several countries.'
                              : `No ${levelLabel(chain[walk.level])} in between.`}
                            testId="wizard-parent-attach-here"
                            onClick={() => startDetails(stepBase)} />
              )}
            </div>

            <Button variant="ghost" size="sm" data-testid="wizard-parent-back"
                    onClick={() => {
                      if (picks.length) setPicks(picks.slice(0, -1));
                      else { setPicks([]); setPhase('type'); }
                    }}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          </div>
        )}

        {/* ── Step 3: the level's own fields ── */}
        {phase === 'details' && (
          <div className="space-y-5" data-testid="wizard-step-details">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"
                 data-testid="wizard-details-path">
              {[...path.map((p) => p.name), form.name || `New ${choice.title}`].join('  →  ')}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="wiz-name">{choice.title} name *</Label>
              <Input id="wiz-name" data-testid="wizard-field-name" value={form.name || ''}
                     onChange={set('name')} autoFocus
                     placeholder={choice.type === 'assembly' ? 'e.g. Mabelreign Assembly' : `e.g. ${choice.title}`} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(typeDef?.fields || []).filter((f) => f !== 'countries').map((f) => (
                <div key={f} className="space-y-1.5">
                  <Label htmlFor={`wiz-${f}`} className="text-xs">{FIELD_LABELS[f] || f}</Label>
                  <Input id={`wiz-${f}`} data-testid={`wizard-field-${f}`}
                         value={form[f] || ''} onChange={set(f)} />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label htmlFor="wiz-leader-name" className="text-xs">Leader</Label>
                <Input id="wiz-leader-name" data-testid="wizard-field-leader-name"
                       value={form.leader_name || ''} onChange={set('leader_name')}
                       placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wiz-leader-title" className="text-xs">Leader title</Label>
                <Input id="wiz-leader-title" data-testid="wizard-field-leader-title"
                       value={form.leader_title || ''} onChange={set('leader_title')}
                       placeholder="left blank uses the default for this level" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wiz-code" className="text-xs">Code</Label>
                <Input id="wiz-code" data-testid="wizard-field-code" value={form.code || ''}
                       onChange={set('code')} placeholder="auto-generated when blank" />
              </div>
            </div>

            {choice.type === 'province' && parentNode?.organisation_type !== 'country' && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-4 space-y-3"
                   data-testid="wizard-countries">
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">
                  Countries served
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(form.countries || []).map((c) => (
                    <Badge key={c} variant="outline" className="bg-white gap-1">
                      {c}
                      <button type="button" className="text-slate-400 hover:text-rose-600"
                              onClick={() => setForm((p) => ({
                                ...p, countries: p.countries.filter((x) => x !== c) }))}>×</button>
                    </Badge>
                  ))}
                  {(form.countries || []).length === 0 && (
                    <span className="text-xs text-slate-500">None added yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input value={countryDraft} data-testid="wizard-country-input"
                         onChange={(e) => setCountryDraft(e.target.value)}
                         placeholder="Add a country and press Enter"
                         onKeyDown={(e) => {
                           if (e.key !== 'Enter') return;
                           e.preventDefault();
                           const v = countryDraft.trim();
                           if (v && !(form.countries || []).includes(v)) {
                             setForm((p) => ({ ...p, countries: [...(p.countries || []), v] }));
                           }
                           setCountryDraft('');
                         }} />
                  <Button type="button" variant="outline" data-testid="wizard-country-add"
                          onClick={() => {
                            const v = countryDraft.trim();
                            if (v && !(form.countries || []).includes(v)) {
                              setForm((p) => ({ ...p, countries: [...(p.countries || []), v] }));
                            }
                            setCountryDraft('');
                          }}>Add</Button>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="wiz-desc" className="text-xs">Description</Label>
              <Textarea id="wiz-desc" rows={3} data-testid="wizard-field-description"
                        value={form.description || ''} onChange={set('description')} />
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" size="sm" data-testid="wizard-details-back"
                      onClick={() => setPhase(choice.type === 'global_ministry' || defaultParent
                        ? 'type' : 'parent')}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
              </Button>
              <Button data-testid="wizard-details-next"
                      onClick={() => ((form.name || '').trim()
                        ? setPhase('review')
                        : toast.error('Give it a name first'))}>
                Review <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 4: review the path ── */}
        {phase === 'review' && (
          <div className="space-y-5" data-testid="wizard-step-review">
            <div className="rounded-xl border border-slate-900/10 bg-slate-900 p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Full hierarchy path
              </p>
              <p className="mt-2 text-sm text-white leading-relaxed" data-testid="wizard-review-path">
                {path.map((p) => (
                  <span key={p.id}>
                    <span className="text-slate-300">{p.name}</span>
                    <span className="text-slate-500 mx-1.5">→</span>
                  </span>
                ))}
                <span className="font-semibold">{form.name}</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Badge className="bg-white/10 text-white border-0 text-[10px]">
                  {typeDef?.label || choice.type}
                </Badge>
                {choice.plant && (
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-0 text-[10px]"
                         data-testid="wizard-review-plant-badge">
                    Church plant
                  </Badge>
                )}
                {!parentNode && (
                  <Badge className="bg-white/10 text-white border-0 text-[10px]">Top of the tree</Badge>
                )}
              </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm"
                data-testid="wizard-review-fields">
              {[['Name', form.name],
                ['Code', form.code || 'auto-generated'],
                ['Leader', form.leader_name],
                ['Leader title', form.leader_title],
                ...(typeDef?.fields || []).filter((f) => f !== 'countries')
                  .map((f) => [FIELD_LABELS[f] || f, form[f]]),
                ['Countries served', (form.countries || []).join(', ')],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-slate-900 font-medium text-right truncate">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="flex justify-between">
              <Button variant="ghost" size="sm" onClick={() => setPhase('details')}
                      data-testid="wizard-review-back">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
              </Button>
              <Button onClick={submit} disabled={save.isPending} data-testid="wizard-submit">
                {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create {choice.title}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrganisationWizard;
